import { appConfig } from 'constants/appConfig';
import { alphaSessionToken } from 'services/alpha/alphaSession';
import {
  ALPHA_NO_RESPONSE_MESSAGE,
  ALPHA_UNREACHABLE_MESSAGE,
  AlphaEvent,
} from 'services/alpha/types';
const WATCHDOG_MS = 60000;
const STREAM_IDLE_MS = 25000;
const STREAM_POLL_MS = 4000;
const STREAM_MAX_WAIT_MS = 180000;
const STREAM_END_RECOVERY_ATTEMPTS = 3;
const STREAM_END_RECOVERY_DELAY_MS = 2000;

export type AlphaStreamRecovery = {
  fetchReply: () => Promise<boolean>;
  isCurrent?: () => boolean;
  idleMs?: number;
  pollMs?: number;
  maxWaitMs?: number;
  endRecoveryDelayMs?: number;
  headersTimeoutMs?: number;
};

interface IHeadersDeadline {
  signal?: AbortSignal;
  expired: () => boolean;
  reached: () => void;
  release: () => void;
}

const headersDeadline = (
  signal: AbortSignal | undefined,
  ms: number | undefined,
): IHeadersDeadline => {
  if (ms === undefined) {
    return { signal, expired: () => false, reached: () => undefined, release: () => undefined };
  }
  const controller = new AbortController();
  let expired = false;
  const follow = (): void => {
    controller.abort();
  };
  const timer = setTimeout(() => {
    expired = true;
    controller.abort();
  }, ms);
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', follow);
  return {
    signal: controller.signal,
    expired: () => expired,
    reached: () => {
      clearTimeout(timer);
    },
    release: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', follow);
    },
  };
};

type ReadResult = { value?: Uint8Array; done: boolean };

const clientError = (message: string): AlphaEvent => ({ type: 'error', message, origin: 'client' });

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const safeFetchReply = async (recovery: AlphaStreamRecovery) => {
  try {
    return await recovery.fetchReply();
  } catch {
    return false;
  }
};

class WatchdogTimeout extends Error {}

async function raceWithWatchdog<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new WatchdogTimeout()), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer!);
  }
}

async function readJsonMessage(res: Response): Promise<string | undefined> {
  try {
    const data = (await res.clone().json()) as { message?: string };
    return data?.message;
  } catch {
    return undefined;
  }
}

export function parseSseChunk(buffer: string): { events: AlphaEvent[]; rest: string } {
  const events: AlphaEvent[] = [];
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';

  parts.forEach(part => {
    const dataLines = part
      .split('\n')
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trim());

    if (dataLines.length === 0) return;

    try {
      const frame: unknown = JSON.parse(dataLines.join('\n'));
      if (typeof frame === 'object' && frame !== null && 'origin' in frame) {
        const copy: Record<string, unknown> = { ...frame };
        delete copy.origin;
        events.push(copy as AlphaEvent);
        return;
      }
      events.push(frame as AlphaEvent);
    } catch {
    }
  });

  return { events, rest };
}

async function consumeAlphaStream(
  res: Response,
  onEvent: (event: AlphaEvent) => void,
): Promise<void> {
  if (res.status === 429) {
    const retryAfterSeconds = Number(res.headers.get('Retry-After')) || 30;
    const message = await readJsonMessage(res);
    onEvent({ type: 'rate_limited', retryAfterSeconds, message });
    return;
  }

  if (res.status === 409) {
    onEvent({ type: 'turn_in_progress' });
    return;
  }

  if (!res.ok || !res.body) {
    onEvent(clientError(ALPHA_UNREACHABLE_MESSAGE));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    let value: Uint8Array | undefined;
    let done: boolean;
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential stream reads, cannot parallelize
      ({ value, done } = await raceWithWatchdog(reader.read(), WATCHDOG_MS));
    } catch (err) {
      if (err instanceof WatchdogTimeout) {
        await reader.cancel().catch(() => {});
        onEvent({ type: 'timeout' });
        return;
      }
      throw err;
    }
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parsed = parseSseChunk(buffer);
    buffer = parsed.rest;
    parsed.events.forEach(onEvent);
  }
}

export async function streamAlpha(
  url: string,
  body: unknown,
  onEvent: (event: AlphaEvent) => void,
  signal?: AbortSignal,
  recovery?: AlphaStreamRecovery,
): Promise<void> {
  const superseded = (): boolean => recovery?.isCurrent?.() === false;
  const deadline = headersDeadline(signal, recovery?.headersTimeoutMs);
  try {
    const base = (appConfig.baseUrl ?? '').replace(/\/$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    const res = await fetch(`${base}${path}`, {
      method: 'POST',
      signal: deadline.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${alphaSessionToken()}`,
      },
      body: JSON.stringify(body),
    });
    deadline.reached();

    if (res.status === 429) {
      const retryAfterSeconds = Number(res.headers.get('Retry-After')) || 30;
      const message = await readJsonMessage(res);
      onEvent({ type: 'rate_limited', retryAfterSeconds, message });
      return;
    }

    if (res.status === 409) {
      onEvent({ type: 'turn_in_progress' });
      return;
    }

    if (!res.ok || !res.body) {
      onEvent(clientError(ALPHA_UNREACHABLE_MESSAGE));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const idleMs = recovery?.idleMs ?? STREAM_IDLE_MS;
    const pollMs = recovery?.pollMs ?? STREAM_POLL_MS;
    const maxWaitMs = recovery?.maxWaitMs ?? STREAM_MAX_WAIT_MS;
    let buffer = '';
    let sawTerminal = false;
    let pendingRead: Promise<ReadResult> | undefined;
    let stalledSince: number | undefined;

    for (;;) {
      let result: ReadResult | undefined;

      if (recovery) {
        if (!pendingRead) pendingRead = reader.read();
        // eslint-disable-next-line no-await-in-loop -- sequential stream reads, cannot parallelize
        result = await Promise.race([
          pendingRead,
          sleep(stalledSince === undefined ? idleMs : pollMs).then(() => undefined),
        ]);
        if (result === undefined) {
          if (stalledSince === undefined) stalledSince = Date.now() - idleMs;
          // eslint-disable-next-line no-await-in-loop -- polling is sequential by design
          if (await safeFetchReply(recovery)) {
            reader.cancel().catch(() => {});
            return;
          }
          if (Date.now() - stalledSince >= maxWaitMs) {
            reader.cancel().catch(() => {});
            if (!superseded()) onEvent({ type: 'timeout' });
            return;
          }
          // eslint-disable-next-line no-continue
          continue;
        }
        pendingRead = undefined;
        stalledSince = undefined;
      } else {
        try {
          // eslint-disable-next-line no-await-in-loop -- sequential stream reads, cannot parallelize
          result = await raceWithWatchdog(reader.read(), WATCHDOG_MS);
        } catch (err) {
          if (err instanceof WatchdogTimeout) {
            await reader.cancel().catch(() => {});
            onEvent({ type: 'timeout' });
            return;
          }
          throw err;
        }
      }

      const { value, done } = result;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSseChunk(buffer);
      buffer = parsed.rest;
      for (let i = 0; i < parsed.events.length; i += 1) {
        if (parsed.events[i].type === 'done' || parsed.events[i].type === 'error') sawTerminal = true;
        onEvent(parsed.events[i]);
      }
    }

    if (!sawTerminal && recovery) {
      for (let attempt = 0; attempt < STREAM_END_RECOVERY_ATTEMPTS; attempt += 1) {
        if (superseded()) return;
        // eslint-disable-next-line no-await-in-loop -- polling is sequential by design
        if (await safeFetchReply(recovery)) return;
        // eslint-disable-next-line no-await-in-loop -- polling is sequential by design
        await sleep(recovery.endRecoveryDelayMs ?? STREAM_END_RECOVERY_DELAY_MS);
      }
      if (superseded()) return;
      onEvent(clientError(ALPHA_UNREACHABLE_MESSAGE));
    }
  } catch (error) {
    const unanswered = deadline.expired() && !superseded();
    if (!unanswered && (error as { name?: string })?.name === 'AbortError') return;
    if (recovery && (await safeFetchReply(recovery))) return;
    if (superseded()) return;
    onEvent(clientError(unanswered ? ALPHA_NO_RESPONSE_MESSAGE : ALPHA_UNREACHABLE_MESSAGE));
  } finally {
    deadline.release();
  }
}

export async function streamAlphaVoiceEvents(
  url: string,
  onEvent: (event: AlphaEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const base = (appConfig.baseUrl ?? '').replace(/\/$/, '');
    const path = url.startsWith('/') ? url : `/${url}`;
    const res = await fetch(`${base}${path}`, {
      method: 'GET',
      signal,
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${alphaSessionToken()}`,
      },
    });

    await consumeAlphaStream(res, onEvent);
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') return;
    onEvent(clientError(ALPHA_UNREACHABLE_MESSAGE));
  }
}
